import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useNotifications } from '@/hooks/use-notifications';
import Widget from '@/components/common/widget.tsx';
import { updateContributorSchemaFactory } from '@/schemas/contributors/update-contributor.ts';
import {
  SESSION_CONTRIBUTORS_ROLES,
  type ContributorDto,
  type ContributorRole,
} from '@/types/dtos/contributors/response/contributor.dto.ts';
import usePatchContributorMutation from '@/state/mutations/contributors/use-patch-contributor.ts';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { type MouseEvent, type ReactNode, useCallback, useMemo, useState, useEffect } from 'react';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import usePatchContributorRoleMutation from '@/state/mutations/contributors/use-patch-contributor-role';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useDeleteEntityContributor } from '@/hooks/use-delete-entity-contributor';
import { RemoveCircleOutline } from '@mui/icons-material';
import { useEventStore } from '@/state/stores/use-event-store';
import { useDeleteContributorRoleSlotsByEventSlotMutation } from '@/state/mutations/contributors/use-delete-contributor-role-slots-by-event-slot';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useSearchParams } from 'react-router';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { useDeleteEventContributorRoles } from '@/state/mutations/contributors/use-delete-event-contributor-roles';

const UpdateContributorForm = ({
  entityId,
  entityType,
  contributor,
  onFlowSlotActionClick,
  showFlowSlotAction,
  flowSlotActionIcon,
  flowSlotActionTooltip,
  hideDeleteButton,
  forceMasterMode,
  eventSlotIdOverride,
  inviteStatusPanel,
  suppressWidgetIconButtons,
  showEmailField,
  fieldsLayout = 'full',
}: {
  entityId: string;
  entityType: 'Session' | 'Flow' | 'Event';
  contributor: ContributorDto;
  onFlowSlotActionClick?: () => void;
  showFlowSlotAction?: boolean;
  flowSlotActionIcon?: React.ReactNode;
  flowSlotActionTooltip?: string;
  hideDeleteButton?: boolean;
  forceMasterMode?: boolean;
  eventSlotIdOverride?: string;
  /** Shown above the form (e.g. contribution / time-slot invite status in flow detail). */
  inviteStatusPanel?: ReactNode;
  /** When true, delete / flow-slot actions live in the collapsible header instead. */
  suppressWidgetIconButtons?: boolean;
  /** When true, show email as the last field and allow PATCH updates (e.g. session actors list). */
  showEmailField?: boolean;
  /** `emailOnly` — slot-assets column. `scheduleActor` — flow schedule: name, role, title, org (no email). `nameEmail` — name + email only (no title/organization). `none` — no fields. */
  fieldsLayout?: 'full' | 'emailOnly' | 'scheduleActor' | 'nameEmail' | 'none';
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const mode = useEventMode();
  const [searchParams] = useSearchParams();
  const eventPlanSlots = useEventStore((s) => s.eventPlan?.slots);
  const hasPlanSlotData = !!eventPlanSlots;
  const eventFlows = useEventStore((s) => s.eventFlows);
  const flowStoreFlow = useFlowStore((s) => s.flowPlan || s.flow);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutateAsync: patchContributorMutation, isPending: isPatchContributorPending } = usePatchContributorMutation();
  const { mutateAsync: patchContributorRoleMutation, isPending: isPatchContributorRolePending } =
    usePatchContributorRoleMutation();
  const deleteEntityContributor = useDeleteEntityContributor();
  const { mutateAsync: deleteRoleSlotsByEventSlot } = useDeleteContributorRoleSlotsByEventSlotMutation();
  const { mutateAsync: deleteContributorRoles } = useDeleteEventContributorRoles();
  const queryClient = useQueryClient();

  const normalizeId = (value?: unknown) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if ('id' in value && typeof (value as { id?: string }).id === 'string') {
        return (value as { id: string }).id;
      }
      if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
        return (value as { _id: string })._id;
      }
      if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
        return (value as { $oid: string }).$oid;
      }
    }
    return undefined;
  };

  const updateContributorSchema = updateContributorSchemaFactory(t);
  type ContributorFormType = z.infer<typeof updateContributorSchema>;
  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isDirty },
  } = useForm<ContributorFormType>({
    resolver: zodResolver(updateContributorSchema),
    defaultValues: {
      role: '',
      name: '',
      position: '',
      organization: '',
      email: '',
    },
  });

  const currentRole = useMemo(() => {
    return contributor.roles?.find((role) => {
      const roleEntityId = normalizeId(role.entity);
      const roleEntityOrigin =
        typeof role.entity === 'object' && role.entity ? normalizeId((role.entity as { origin?: unknown }).origin) : '';
      return roleEntityId === entityId || roleEntityOrigin === entityId;
    });
  }, [contributor.roles, entityId]);

  const flowForDelete = useMemo(() => {
    if (entityType !== 'Flow') return undefined;
    if (flowStoreFlow && (flowStoreFlow.id === entityId || flowStoreFlow.origin === entityId)) {
      return flowStoreFlow;
    }
    return eventFlows?.find((flow) => flow.id === entityId || flow.origin === entityId);
  }, [entityId, entityType, eventFlows, flowStoreFlow]);

  const eventSlotIdParam = eventSlotIdOverride ?? (searchParams.get('eventSlotId') || undefined);
  const flowSlotId = normalizeId(flowForDelete?.slot?.id);
  const flowSlotOriginId = normalizeId(flowForDelete?.slot?.origin);
  const hasFlowSlotContext = !!(flowSlotId || flowSlotOriginId);
  const isMasterMode = forceMasterMode || (!eventSlotIdParam && !hasFlowSlotContext);

  const flowSlotCount = useMemo(() => {
    if (!eventPlanSlots || entityType !== 'Flow') return 0;
    const flowIds = new Set<string>(
      [normalizeId(entityId), normalizeId(flowForDelete?.origin), normalizeId(flowForDelete?.id)].filter(
        Boolean,
      ) as string[],
    );
    return eventPlanSlots.filter((slot) => {
      if (slot.deleted === true) return false;
      if (slot.contentType !== EVENT_SLOT_TYPE.FLOW) return false;
      const slotContentId = normalizeId(slot.content);
      const slotContentOrigin =
        typeof slot.content === 'object' && slot.content
          ? normalizeId((slot.content as { origin?: unknown }).origin)
          : undefined;
      if (!slotContentId && !slotContentOrigin) return false;
      return (slotContentId && flowIds.has(slotContentId)) || (slotContentOrigin && flowIds.has(slotContentOrigin));
    }).length;
  }, [entityId, entityType, eventPlanSlots, flowForDelete]);

  const canDeleteFlowRole = entityType === 'Flow' ? isMasterMode || (hasPlanSlotData && flowSlotCount <= 1) : true;

  const formDefaults = useMemo(
    () => ({
      role: currentRole?.role ?? '',
      name: contributor.profiles[0]?.displayName,
      position: contributor.profiles[0]?.title ?? '',
      organization: contributor.profiles[0]?.organization ?? '',
      email: contributor.email ?? '',
    }),
    [contributor, currentRole],
  );

  useEffect(() => {
    resetForm(formDefaults);
  }, [contributor, currentRole, entityId, formDefaults, resetForm]);

  const handleCancel = useCallback(() => {
    resetForm(formDefaults);
  }, [formDefaults, resetForm]);

  const showProfileFields = fieldsLayout === 'full' || fieldsLayout === 'scheduleActor' || fieldsLayout === 'nameEmail';
  const showEmailFieldBlock =
    showEmailField && (fieldsLayout === 'full' || fieldsLayout === 'emailOnly' || fieldsLayout === 'nameEmail');

  const onSubmit = async (data: ContributorFormType) => {
    try {
      const updatedRole = contributor.roles.find((role) => {
        const roleEntityId = normalizeId(role.entity);
        const roleEntityOrigin =
          typeof role.entity === 'object' && role.entity
            ? normalizeId((role.entity as { origin?: unknown }).origin)
            : '';
        return roleEntityId === entityId || roleEntityOrigin === entityId;
      });
      if (
        updatedRole &&
        entityType === 'Session' &&
        (fieldsLayout === 'full' || fieldsLayout === 'scheduleActor') &&
        data.role !== updatedRole.role
      ) {
        await patchContributorRoleMutation({
          contributorId: contributor.id,
          roleId: updatedRole.id,
          role: data.role as unknown as ContributorRole,
        });
      }

      const profile = contributor.profiles[0];
      const profileChanged =
        showProfileFields &&
        (profile.displayName !== data.name ||
          (fieldsLayout !== 'nameEmail' &&
            (profile.organization !== data.organization || profile.title !== data.position)));
      const emailChanged = showEmailFieldBlock && (contributor.email ?? '') !== (data.email ?? '');

      if (profileChanged || emailChanged) {
        await patchContributorMutation({
          id: contributor.id,
          ...(profileChanged
            ? {
                profiles: [
                  {
                    ...profile,
                    displayName: data.name,
                    ...(fieldsLayout === 'nameEmail'
                      ? {}
                      : {
                          organization: data.organization,
                          title: data.position,
                        }),
                  },
                ],
              }
            : {}),
          ...(emailChanged ? { email: data.email === '' ? null : data.email } : {}),
        });
      }

      notification.notifySuccess(t('event.detail.addEditContributor.notifications.addEditContributorSuccess'));
    } catch {
      notification.notifyError(t('event.detail.addEditContributor.notifications.addEditContributorFailed'));
    }
  };

  const handleDeleteModalCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteContributor = async () => {
    if (!currentRole) return;
    try {
      if (entityType === 'Flow') {
        if (canDeleteFlowRole) {
          await deleteEntityContributor({
            contributor,
            entityType,
            entityId,
          });
          await queryClient.refetchQueries({
            queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
            type: 'all',
          });
        } else {
          const eventSlotId = eventSlotIdParam || flowSlotId || flowSlotOriginId;
          if (!eventSlotId) {
            throw new Error('Missing event slot context for flow role removal.');
          }
          await deleteRoleSlotsByEventSlot({
            contributorId: contributor.id,
            roleId: currentRole.id,
            eventSlotId,
          });
          await queryClient.refetchQueries({
            queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
            type: 'all',
          });
          const displayName =
            contributor.profiles[0]?.displayName ||
            contributor.email ||
            t('event.detail.contributors.unknownContributor');
          notification.notifySuccess(
            t('event.detail.contributors.notifications.deleteRoleSlotSuccess', { name: displayName }),
          );
        }
      } else if (entityType === 'Event') {
        await deleteContributorRoles({
          contributorId: contributor.id,
          roleIds: [currentRole.id],
        });
        await queryClient.refetchQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
          type: 'all',
        });
        const displayName =
          contributor.profiles[0]?.displayName ||
          contributor.email ||
          t('event.detail.contributors.unknownContributor');
        notification.notifySuccess(t('event.detail.crewWidget.removeEventRoleSuccess', { name: displayName }));
      } else {
        await deleteEntityContributor({
          contributor,
          entityType: 'Session',
          entityId,
        });
      }
      setIsDeleteDialogOpen(false);
    } catch {
      if (entityType === 'Flow' && !canDeleteFlowRole) {
        const displayName =
          contributor.profiles[0]?.displayName ||
          contributor.email ||
          t('event.detail.contributors.unknownContributor');
        notification.notifyError(
          t('event.detail.contributors.notifications.deleteRoleSlotError', { name: displayName }),
        );
      } else if (entityType === 'Event') {
        const displayName =
          contributor.profiles[0]?.displayName ||
          contributor.email ||
          t('event.detail.contributors.unknownContributor');
        notification.notifyError(t('event.detail.crewWidget.removeEventRoleError', { name: displayName }));
      }
    }
  };

  const isPending = useMemo(() => {
    return isPatchContributorPending || isPatchContributorRolePending;
  }, [isPatchContributorPending, isPatchContributorRolePending]);

  const showDirtyActions = isDirty && !isOrganizeMode(mode);

  return (
    <Widget
      iconButtons={
        suppressWidgetIconButtons
          ? []
          : [
              ...(showFlowSlotAction && flowSlotActionIcon && onFlowSlotActionClick
                ? [
                    {
                      icon: flowSlotActionIcon,
                      disabled: isOrganizeMode(mode),
                      onClick: onFlowSlotActionClick,
                      testId: 'flow-slot-action-button',
                      tooltip: flowSlotActionTooltip,
                    },
                  ]
                : []),
              ...(!hideDeleteButton
                ? [
                    {
                      icon: entityType === 'Flow' && !canDeleteFlowRole ? <RemoveCircleOutline /> : <DeleteIcon />,
                      disabled: isOrganizeMode(mode),
                      onClick: () => setIsDeleteDialogOpen(true),
                      testId: `delete-${entityType}-contributor-button`,
                    },
                  ]
                : []),
            ]
      }
    >
      <ConfirmationModal
        open={isDeleteDialogOpen}
        isLoading={false}
        title={
          entityType === 'Session'
            ? t('session.contributors.removeTitle', {
                name:
                  contributor.profiles[0]?.displayName ||
                  contributor.email ||
                  t('event.detail.contributors.unknownContributor'),
              })
            : entityType === 'Event'
              ? t('event.detail.crewWidget.deleteEventRoleTitle', {
                  name:
                    contributor.profiles[0]?.displayName ||
                    contributor.email ||
                    t('event.detail.contributors.unknownContributor'),
                })
              : !canDeleteFlowRole
                ? t('event.detail.contributors.deleteRoleSlotModalTitle', {
                    name:
                      contributor.profiles[0]?.displayName ||
                      contributor.email ||
                      t('event.detail.contributors.unknownContributor'),
                  })
                : t('flows.detail.contributors.deleteTitle', {
                    name:
                      contributor.profiles[0]?.displayName ||
                      contributor.email ||
                      t('event.detail.contributors.unknownContributor'),
                  })
        }
        text={
          entityType === 'Session'
            ? t('session.contributors.removeText', {
                name:
                  contributor.profiles[0]?.displayName ||
                  contributor.email ||
                  t('event.detail.contributors.unknownContributor'),
              })
            : entityType === 'Event'
              ? t('event.detail.crewWidget.deleteEventRoleText', {
                  name:
                    contributor.profiles[0]?.displayName ||
                    contributor.email ||
                    t('event.detail.contributors.unknownContributor'),
                })
              : !canDeleteFlowRole
                ? t('event.detail.contributors.deleteRoleSlotModalText', {
                    name:
                      contributor.profiles[0]?.displayName ||
                      contributor.email ||
                      t('event.detail.contributors.unknownContributor'),
                  })
                : t('flows.detail.contributors.deleteText', {
                    name:
                      contributor.profiles[0]?.displayName ||
                      contributor.email ||
                      t('event.detail.contributors.unknownContributor'),
                  })
        }
        onConfirm={handleDeleteContributor}
        onCancel={handleDeleteModalCancel}
      />
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={1} sx={{ flex: 1 }}>
        {inviteStatusPanel}
        {showDirtyActions ? (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={handleCancel}
              data-testid="cancel-updated-actor-button"
            >
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="save-updated-actor-button"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              startIcon={isPending ? <CircularProgress size={20} /> : undefined}
              disabled={isPending}
              size="small"
              color="secondary"
            >
              {t('common.save')}
            </Button>
          </Stack>
        ) : null}
        {showProfileFields ? (
          <>
            <Grid
              container
              spacing={1}
              alignItems="flex-start"
              sx={{ mt: inviteStatusPanel || showDirtyActions ? 0 : 1 }}
            >
              <Grid size={{ xs: entityType === 'Session' ? 8 : 12 }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={6}
                      variant="standard"
                      disabled={isOrganizeMode(mode)}
                      label={t('contributor.form.nameInputLabel')}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              {entityType === 'Session' && (
                <Grid size={{ xs: 4 }} display="flex" direction="row" alignItems="flex-start">
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth variant="standard" error={!!errors.role}>
                        <InputLabel>{t('contributor.form.roleInputLabel', { type: entityType })}</InputLabel>
                        <Select {...field} disabled={isOrganizeMode(mode)}>
                          {SESSION_CONTRIBUTORS_ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                              {t(`common.roleLabels.${role}`)}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.role && (
                          <Typography variant="caption" color="error">
                            {errors.role.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
            </Grid>
            {(fieldsLayout === 'full' || fieldsLayout === 'scheduleActor') && (
              <>
                <Controller
                  name="position"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      disabled={isOrganizeMode(mode)}
                      variant="standard"
                      label={t('contributor.form.positionInputLabel')}
                      error={!!errors.position}
                      helperText={errors.position?.message}
                    />
                  )}
                />
                <Controller
                  name="organization"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      disabled={isOrganizeMode(mode)}
                      variant="standard"
                      label={t('contributor.form.organizationInputLabel')}
                      error={!!errors.organization}
                      helperText={errors.organization?.message}
                    />
                  )}
                />
              </>
            )}
          </>
        ) : null}
        {showEmailFieldBlock && (
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                disabled={isOrganizeMode(mode)}
                variant="standard"
                type="email"
                autoComplete="email"
                label={t('contributor.form.emailInputLabel')}
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={fieldsLayout === 'emailOnly' ? { mt: inviteStatusPanel || showDirtyActions ? 0 : 1 } : undefined}
              />
            )}
          />
        )}
      </Stack>
    </Widget>
  );
};

export default UpdateContributorForm;
